// Blog content - imported from content/blog/
// MDX files are compiled to React components at build time

import { ComponentType } from "react";
import blogMetadata from "./blog-metadata.json";

// Import all blog posts
// NOTE: Add new imports here when creating new posts
import WhatIsAChatgptAppMdx from "@/content/blog/what-is-a-chatgpt-app.mdx";

export interface BlogHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  description: string;
  category: string | null;
  tags: string[];
  ogImage: string | null;
  headings: BlogHeading[];
  readTime: number;
  rawContent: string;
  Component: ComponentType;
}

type MetadataPost = {
  slug: string;
  title: string;
  date: string;
  description: string;
  category: string | null;
  tags: string[];
  ogImage: string | null;
  headings: BlogHeading[];
  readTime: number;
  rawContent: string;
};

// Map slugs to MDX components
// NOTE: Add new mappings here when creating new posts
const componentMap: Record<string, ComponentType> = {
  "what-is-a-chatgpt-app": WhatIsAChatgptAppMdx,
};

const metadata = blogMetadata as { posts: MetadataPost[] };

// Combine metadata with components
export const blogPosts: BlogPost[] = metadata.posts
  .map((post) => {
    const Component = componentMap[post.slug];
    if (!Component) {
      console.warn(`[Blog] No component found for post "${post.slug}"`);
      return null;
    }
    return {
      ...post,
      Component,
    };
  })
  .filter((post): post is BlogPost => post !== null);

// Helper to get a single post by slug
export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

// Helper to get posts by tag
export function getBlogPostsByTag(tag: string): BlogPost[] {
  return blogPosts.filter((post) =>
    post.tags.map((t) => t.toLowerCase()).includes(tag.toLowerCase()),
  );
}

// Helper to get posts by category
export function getBlogPostsByCategory(category: string): BlogPost[] {
  return blogPosts.filter(
    (post) => post.category?.toLowerCase() === category.toLowerCase(),
  );
}

// Helper to get all unique categories
export function getAllCategories(): string[] {
  return Array.from(
    new Set(
      blogPosts
        .map((post) => post.category)
        .filter((cat): cat is string => cat !== null),
    ),
  ).sort();
}

// Helper to format date for display
export function formatBlogDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
